import type { TableColumnsType } from 'antd';
import classNames from 'classnames';

import { COLOR } from '@/shared/design-tokens/design-tokens';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import {
  CONNECTOR_STATUSES,
  type ConnectorSortField,
  type ConnectorStatus,
  type SortOrder,
} from '@/entities/connector';

import type { ConnectorTableRow } from '@/widgets/connectors/ConnectorTableContent/model/types';

import styles from './connectorTableColumns.module.css';

const STATUS_LABELS: Record<Exclude<ConnectorStatus, null>, string> = {
  [CONNECTOR_STATUSES.SUCCESS]: 'Success',
  [CONNECTOR_STATUSES.FAILURE]: 'Failure',
} as const;

const toAntdSortOrder = (
  columnKey: ConnectorSortField,
  sortBy?: ConnectorSortField,
  sortOrder?: SortOrder
): 'ascend' | 'descend' | undefined => {
  if (sortBy !== columnKey) return undefined;
  return sortOrder === 'asc' ? 'ascend' : 'descend';
};

type GetConnectorTableColumnsParams = {
  onEdit: (row: ConnectorTableRow) => void;
  sortBy?: ConnectorSortField;
  sortOrder?: SortOrder;
};

export const getConnectorTableColumns = ({
  onEdit,
  sortBy,
  sortOrder,
}: GetConnectorTableColumnsParams): TableColumnsType<ConnectorTableRow> => {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 708,
      sorter: true,
      sortOrder: toAntdSortOrder('name', sortBy, sortOrder),
      render: (name: string) => (
        <EllipsisText text={name} className={styles.nameCell} tooltipPlacement="bottomRight" arrow={false} />
      ),
    },
    {
      title: 'Worker',
      dataIndex: 'workerName',
      key: 'workerName',
      width: 708,
      render: (workerName?: string) =>
        workerName ? (
          <EllipsisText text={workerName} className={styles.workerCell} tooltipPlacement="bottomRight" arrow={false} />
        ) : (
          '-'
        ),
    },
    {
      title: 'Activation',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 136,
      sorter: true,
      sortOrder: toAntdSortOrder('isActive', sortBy, sortOrder),
      render: (isActive: boolean) => (
        <div
          className={classNames(
            styles.activationContainer,
            isActive ? styles.activationActive : styles.activationInactive
          )}>
          {isActive ? (
            <Icon name="light-on" width={16} height={16} color={COLOR.PURPLE[50]} />
          ) : (
            <Icon name="light" width={16} height={16} color={COLOR.GRAY[10]} />
          )}
          <span className={styles.activationLabel}>{isActive ? 'Activation' : 'Deactivation'}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      sortOrder: toAntdSortOrder('status', sortBy, sortOrder),
      align: 'center',
      onHeaderCell: () => ({ className: styles.statusHeaderCell }),
      render: (status: ConnectorStatus, record: ConnectorTableRow) => {
        if (!record.isActive || status === null) return '-';

        return (
          <div
            className={classNames(
              styles.statusContainer,
              status === CONNECTOR_STATUSES.SUCCESS ? styles.statusSuccess : styles.statusFailure
            )}>
            {status === CONNECTOR_STATUSES.SUCCESS ? (
              <Icon name="circle-check" width={16} height={16} size={16} color={COLOR.EMERALD[40]} />
            ) : (
              <Icon name="cancel" width={16} height={16} size={16} color={COLOR.RED[50]} />
            )}
            <span className={styles.statusLabel}>{STATUS_LABELS[status]}</span>
          </div>
        );
      },
    },
    {
      title: 'Edit',
      key: 'edit',
      width: 60,
      align: 'start',
      onHeaderCell: () => ({ className: styles.editColumn }),
      render: (_value: unknown, record: ConnectorTableRow) => (
        <IconButton size="small" aria-label="커넥터 편집" onClick={() => onEdit(record)} tooltip="커넥터 편집">
          <Icon name="edit" size={16} />
        </IconButton>
      ),
    },
  ];
};
